import { EntryType, TreeNode } from '@morph-mapper/types';
import { definePlugin } from '../../framework';

type Notation = 'american' | 'european';
type Rounding = 'ceil' | 'floor' | 'leave';

const hasNumberValidation = (validation: unknown): boolean =>
  (validation as { _def?: { typeName?: string } } | undefined)?._def?.typeName === 'ZodNumber';

const semanticsIndicateNumeric = (semantics: unknown): boolean =>
  typeof semantics === 'string' && /\bnumeric/i.test(semantics);

const KNOWN_NUMERIC_FIELD_KEYS = ['weight'];

const isKnownNumericKey = (key: unknown): boolean =>
  typeof key === 'string' && KNOWN_NUMERIC_FIELD_KEYS.includes(key.trim().toLowerCase());

export const isNumericIntentField = (entry: Pick<TreeNode, 'key' | 'validation' | 'semantics'>): boolean =>
  hasNumberValidation(entry.validation) ||
  semanticsIndicateNumeric(entry.semantics) ||
  isKnownNumericKey(entry.key);

const toNotation = (value: unknown): Notation =>
  String(value).trim().toLowerCase() === 'european' ? 'european' : 'american';

const toRounding = (value: unknown): Rounding => {
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'floor') return 'floor';
  if (normalized === 'leave') return 'leave';
  return 'ceil';
};

const extractRawValue = (value: unknown): { prefix: string; raw: string } | null => {
  if (typeof value !== 'string') return null;
  const match = value.match(/^(c\d+:\s*)(.*)$/);
  if (match) return { prefix: match[1], raw: match[2].trim() };
  return { prefix: '', raw: value.trim() };
};

const parseNumeric = (raw: string, notation: Notation): number | null => {
  const cleaned =
    notation === 'european'
      ? raw.replace(/\./g, '').replace(/,/g, '.')
      : raw.replace(/,/g, '');

  if (!cleaned.trim() || Number.isNaN(Number(cleaned))) return null;

  return Number(cleaned);
};

const applyRounding = (value: number, rounding: Rounding): number => {
  if (rounding === 'ceil') return Math.ceil(value);
  if (rounding === 'floor') return Math.floor(value);
  return value;
};

const isWrappedSnippet = (snippet: unknown): snippet is [unknown[]] =>
  Array.isArray(snippet) &&
  Array.isArray(snippet[0]) &&
  snippet[0].length > 0;

const buildNotationSnippet = (source: unknown, notation: Notation) => {
  if (notation === 'european') {
    return {
      '$utils.regexp.replace': [
        { '$utils.regexp.replace': [source, ['[.]', 'g'], ''] },
        ['[,]', 'g'],
        '.',
      ],
    };
  }

  return {
    '$utils.regexp.replace': [source, ['[,]', 'g'], ''],
  };
};

const buildRoundingSnippet = (source: unknown, rounding: Rounding) => {
  if (rounding === 'ceil') return { '$math.ceil': source };
  if (rounding === 'floor') return { '$math.floor': source };
  return source;
};

export const buildNumberSnippet = (
  originalSnippet: unknown,
  notation: Notation,
  rounding: Rounding,
) => {
  const parsed = { '$math.parseFloat': buildNotationSnippet(originalSnippet, notation) };
  return buildRoundingSnippet(parsed, rounding);
};

export const numberHandling = definePlugin<{}>({
  name: 'Number Handling',
  version: '0.1.0',
  description:
    'Converts numeric-looking suggestions on numeric fields into real numbers, stripping separators and rounding as configured.',
  initialState: {},
  settings: {
    notation: {
      name: 'Notation',
      type: 'select',
      default: 'american',
      options: [
        { value: 'american', label: 'American' },
        { value: 'european', label: 'European' },
      ],
    },
    rounding: {
      name: 'Rounding',
      type: 'select',
      default: 'ceil',
      options: [
        { value: 'ceil', label: 'Round up' },
        { value: 'floor', label: 'Round down' },
        { value: 'leave', label: 'Leave' },
      ],
    },
  },

  postFieldSuggestions({ entry, suggestions, settings }) {
    if (!suggestions || suggestions.length === 0) return [];
    if (String(entry.key) === '#iterator') return [];
    if (!isNumericIntentField(entry)) return [];

    const notation = toNotation(settings['notation']);
    const rounding = toRounding(settings['rounding']);

    for (const suggestion of suggestions) {
      if (suggestion.iterator === true) continue;

      const snippet = suggestion.snippet;
      const wrapped = isWrappedSnippet(snippet);
      if (typeof snippet !== 'string' && !wrapped) continue;

      const extracted = extractRawValue(suggestion.value);
      if (extracted === null) continue;

      const numericValue = parseNumeric(extracted.raw, notation);
      if (numericValue === null) continue;

      suggestion.value = extracted.prefix + String(applyRounding(numericValue, rounding));

      if (wrapped) {
        const inner = (snippet as unknown[][])[0][0];
        suggestion.snippet = [[buildNumberSnippet(inner, notation, rounding)]] as any;
      } else {
        suggestion.snippet = buildNumberSnippet(snippet, notation, rounding) as any;
      }
      suggestion.type = EntryType.Code;
    }

    return [];
  },
});
